/**
 * How long a session takes, in the two numbers the builder shows (§7).
 *
 * THERAPY is the sum of what each exercise says it needs — its prescribed
 * duration plus its own instruction screen. Nothing here changes that; it comes
 * from `estimateDurationSec()` on the exercise itself.
 *
 * REALISTIC adds what happens between and after exercises. It used to be
 * `therapy × 1.15`, taken from the friction budget in constraint 4, and that was
 * wrong in shape rather than in size: a transition is the same act every time —
 * an instruction screen, repositioning, one tap on the rating — so it cannot
 * cost more because the exercise before it happened to run longer. Modelled as a
 * percentage it did exactly that, implying anywhere from 10 seconds to 92
 * seconds per transition across ordinary sessions (report, 2026-08-22).
 *
 * Ten seconds is not a new claim. It is what the old multiplier already worked
 * out to for the short-exercise sessions that make up most prescribing; this
 * only stops it drifting with something irrelevant. Every realistic session in
 * the comparison moves by less than half a minute except the long single
 * exercise, which was the case that was wrong.
 *
 * Both constants are placeholders for a measurement. Phase 4 will know real
 * elapsed time against therapy time for every session a patient completes, and
 * should replace them rather than defend them.
 */

import { getDefinition } from '@pathways/exercises';
import type { ProgramSessionDraft } from '../../api.js';

/** Between one exercise and the next: instructions, repositioning, one tap. */
export const TRANSITION_SEC = 10;

/** Once per session, at the end: the close-out symptom rating. */
export const CLOSE_OUT_SEC = 30;

/**
 * §7's soft warning. Not a cap — the doctor may deliberately prescribe longer,
 * and the per-patient session ceiling in the safety settings is the real limit.
 * This is the number that keeps a program from drifting long by accident.
 *
 * Measured against THERAPY time, not the realistic figure. It was checked
 * against the with-transitions number until 2026-08-21, which meant the warning
 * fired at 10:26 of therapy while calling itself twelve minutes.
 */
export const SOFT_WARN_SECONDS = 12 * 60;

export interface SessionTime {
  /** Time inside the exercises, including their own instruction screens. */
  therapy: number;
  /** Therapy plus transitions and the close-out. */
  realistic: number;
  /** Exercises that could be costed — one whose module is gone is skipped. */
  counted: number;
}

export function sessionTime(session: ProgramSessionDraft): SessionTime {
  let therapy = 0;
  let counted = 0;

  for (const exercise of session.exercises) {
    const definition = getDefinition(exercise.exerciseId);
    // A prescription referencing a retired exercise is shown as an error in the
    // builder and cannot be costed. Leaving it out of both figures is the only
    // honest option — inventing a duration for it would be worse.
    if (!definition) continue;
    therapy += definition.estimateDurationSec(exercise.params);
    counted += 1;
  }

  return {
    therapy,
    // An empty session is not a thirty-second session. No exercises, no sitting
    // down, no close-out rating — nothing happens at all.
    realistic: counted === 0 ? 0 : therapy + TRANSITION_SEC * counted + CLOSE_OUT_SEC,
    counted,
  };
}

/** m:ss, which is how a dose of this length is read. */
export function formatTime(seconds: number): string {
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}
